import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Link as MuiLink,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  IconButton,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import ShieldIcon from '@mui/icons-material/Shield';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate, Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { isAgreementCompleted, isSuperAdmin } from '../utils/agreementUtils';

const AdminFooter = () => {
  const { user } = useUser();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const navigate = useNavigate();
  const superAdmin = isSuperAdmin(user);
  const [agreementDone, setAgreementDone] = useState(false);

  useEffect(() => {
    setAgreementDone(isAgreementCompleted(user));

    const handleUpdate = () => {
      setAgreementDone(isAgreementCompleted(user));
    };

    window.addEventListener('gpn_admin_agreement_updated', handleUpdate);
    return () => {
      window.removeEventListener('gpn_admin_agreement_updated', handleUpdate);
    };
  }, [user]);

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        pt: 3,
        pb: 3,
        px: { xs: 2, sm: 3, md: 4 },
        background: isLight ? 'rgba(255, 255, 255, 0.95)' : 'linear-gradient(180deg, rgba(26, 31, 46, 0.4) 0%, rgba(15, 23, 42, 0.85) 100%)',
        borderTop: isLight ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(76, 161, 175, 0.2)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        {/* Left: Branding & Mission */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textAlign: { xs: 'center', md: 'left' } }}>
          <Box
            component="a"
            href="https://glocalprayer.net"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              background: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0.3,
              boxShadow: '0 2px 8px rgba(76, 161, 175, 0.3)',
              textDecoration: 'none',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.1)' },
            }}
          >
            <img
              src="https://glocalprayer.net/prayer/public/assets/img/gp-logo.png"
              alt="GPN"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ color: isLight ? '#0F172A' : '#FFFFFF', fontWeight: 600, fontSize: '0.85rem' }}>
              Glocal Prayer Network <span style={{ color: isLight ? '#2B8290' : '#4CA1AF' }}>Admin Portal</span>
            </Typography>
            <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.55)', display: 'block' }}>
              © {new Date().getFullYear()} GPN. Uniting believers globally in prayer and fellowship.
            </Typography>
          </Box>
        </Box>

        {/* Center / Right: Required Footer Options */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: { xs: 1.5, sm: 2.5 },
          }}
        >
          {/* Terms & Conditions */}
          <MuiLink
            component={Link}
            to="/admin/terms-and-conditions"
            sx={{
              color: isLight ? '#475569' : 'rgba(255, 255, 255, 0.75)',
              fontSize: '0.825rem',
              fontWeight: 500,
              textDecoration: 'none',
              transition: 'all 0.2s',
              '&:hover': {
                color: isLight ? '#2B8290' : '#4CA1AF',
                textDecoration: 'underline',
              },
            }}
          >
            Terms & Conditions
          </MuiLink>

          <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: isLight ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255,255,255,0.2)' }} />

          {/* Privacy Policy */}
          <MuiLink
            component={Link}
            to="/admin/privacy-policy"
            sx={{
              color: isLight ? '#475569' : 'rgba(255, 255, 255, 0.75)',
              fontSize: '0.825rem',
              fontWeight: 500,
              textDecoration: 'none',
              transition: 'all 0.2s',
              '&:hover': {
                color: isLight ? '#2B8290' : '#4CA1AF',
                textDecoration: 'underline',
              },
            }}
          >
            Privacy Policy
          </MuiLink>

          <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: isLight ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255,255,255,0.2)' }} />

          {/* Admin Agreement with Status Alert Indicator */}
          <Box
            onClick={() => navigate('/admin/admin-agreement')}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.8,
              cursor: 'pointer',
              color: superAdmin ? '#38BDF8' : agreementDone ? '#34D399' : '#FBBF24',
              fontSize: '0.825rem',
              fontWeight: 600,
              px: 1.2,
              py: 0.4,
              borderRadius: '16px',
              background: superAdmin
                ? 'rgba(2, 132, 199, 0.15)'
                : agreementDone
                ? 'rgba(16, 185, 129, 0.1)'
                : 'rgba(245, 158, 11, 0.15)',
              border: superAdmin
                ? '1px solid rgba(2, 132, 199, 0.35)'
                : agreementDone
                ? '1px solid rgba(16, 185, 129, 0.3)'
                : '1px solid rgba(245, 158, 11, 0.4)',
              transition: 'all 0.2s',
              '&:hover': {
                background: superAdmin
                  ? 'rgba(2, 132, 199, 0.25)'
                  : agreementDone
                  ? 'rgba(16, 185, 129, 0.2)'
                  : 'rgba(245, 158, 11, 0.25)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            {superAdmin ? (
              <ShieldIcon sx={{ fontSize: 15, color: '#38BDF8' }} />
            ) : agreementDone ? (
              <CheckCircleIcon sx={{ fontSize: 15, color: '#34D399' }} />
            ) : (
              <WarningAmberIcon sx={{ fontSize: 15, color: '#FBBF24' }} />
            )}
            <span>Admin Agreement</span>
            <Chip
              size="small"
              label={superAdmin ? 'Compliance Tracker' : agreementDone ? 'Active' : 'Pending'}
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 700,
                background: superAdmin ? '#0284C7' : agreementDone ? '#10B981' : '#F59E0B',
                color: '#FFFFFF',
              }}
            />
          </Box>

          <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }} />

          {/* Guide Section */}
          <MuiLink
            component={Link}
            to="/admin/guide"
            sx={{
              color: 'rgba(255, 255, 255, 0.75)',
              fontSize: '0.825rem',
              fontWeight: 500,
              textDecoration: 'none',
              transition: 'all 0.2s',
              '&:hover': {
                color: '#4CA1AF',
                textDecoration: 'underline',
              },
            }}
          >
            Guide & Help
          </MuiLink>

          <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }} />

          {/* External GPN Website Link */}
          <MuiLink
            href="https://glocalprayer.net"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: '#4CA1AF',
              fontSize: '0.825rem',
              fontWeight: 600,
              textDecoration: 'none',
              transition: 'all 0.2s',
              '&:hover': {
                color: '#6DD5FA',
                textDecoration: 'underline',
              },
            }}
          >
            glocalprayer.net <LaunchIcon sx={{ fontSize: 13 }} />
          </MuiLink>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminFooter;
